/* eslint-disable camelcase */
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { PortControl } from './PortControl'
import { Port } from '../../types/models'
import { Em3PhConfig, Em3PhPort, PortConfigEm3Ph } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { ChipSelector } from '../../components/atoms/ChipSelector'
import { ImportExport, Straight } from '@mui/icons-material'
import { HelpTitle } from '../../components/atoms/HelpTitle'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { renderNetMeterCountWarning } from './netMeterWarning'

/**
 * Controller for 3-phase power meter.
 */
export class P3PowerMeterPortControl extends PortControl<Em3PhPort, Em3PhConfig> {

  port: Em3PhPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as Em3PhPort
  }

  getDescription(): string { return this.app.t('Energy consumption measurement using 3 current sensors') }

  getTypeName(): string { return this.app.t('PortType-3-phase Power Meter') }

  get group(): string { return this.app.t('PortGroup-Energy Metering') }

  getConfigDefaults(): PortConfigEm3Ph {
    return {
      enabled: false,
      enable_phase_sensing: true,
      to_summer_inverted: false,
      ma_per_v: [50000, 50000, 50000]
    }
  }

  infoTop() : React.ReactNode | undefined {
    const value = this.getConfig('enable_phase_sensing') ? 'BI' : 'UNI'

    const onSelect = (value: string) => {
      if (value === 'BI') {
        this.setConfig('enable_phase_sensing', true)
      } else {
        this.setConfig('enable_phase_sensing', false)
      }
    }

    const maPerV: number[] = this.getConfig('ma_per_v')
    const onChangeSensitivity = (value: string) => {
      const num = parseInt(value.replace(/[^0-9]/g, '') || '0') * 1000
      // Workaround: For some reason `Autocomplete`triggers onInputChange immediately on the first render.
      const old: number[] = this.getConfig('ma_per_v')
      if (old && old.length === 3 && old[0] === num && old[1] === num && old[2] === num) {
        return
      }
      this.setConfig('ma_per_v', [num, num, num])
    }

    const toSummerInverted = this.getConfig('to_summer_inverted')

    return (
      <Box>
        <ChipSelector
          sx={{ mt: 1 }}
          items={{
            UNI: this.app.t('Unidirectional metering with current transformer'),
            BI: this.app.t('Bidirectional metering with phase sensor')
          }}
          icons={[Straight, ImportExport]}
          selected={value || null}
          onSelect={(value) => onSelect(value)}
          onePerLine
          more={3}
        />
        <Box sx={{ mt: 2 }}>
          <HelpTitle sx={{ mr: '50%' }} title={''} code="EM_3PH"/>

          <Box sx={{ display: 'flex' }}>
            <CustomSwitch data-cy="switch-To Summer" onClick={() => this.setConfig('to_summer_inverted', !toSummerInverted)} checked={!toSummerInverted}/>
            <Typography component="span" sx={{ ml: 1, mt: 1 }}>
              <Trans>Part of building net metering?</Trans>
            </Typography>
          </Box>

          {renderNetMeterCountWarning(this)}

          <Trans>Sensor A/V</Trans>

          <Autocomplete
            sx={{ my: 2, width: '50%' }}
            data-cy="text-Sensitivity"
            freeSolo
            options={['30', '50']}
            value={`${maPerV && maPerV.length ? maPerV[0] / 1000 : ''}`}
            renderInput={(params) => <TextField {...params}/>}
            onInputChange={(_, value) => onChangeSensitivity(value)}
          />
        </Box>
      </Box>
    )
  }
}
